import { useState, useEffect, useRef, forwardRef } from "react";
import { Search, X } from "lucide-react";
import styles from "./SearchInput.module.css";

const SearchInput = forwardRef(
  (
    {
      value: controlledValue,
      onChange,
      onClear,
      placeholder = "Search...",
      debounceMs = 300,
      className = "",
      fullWidth = false,
      style,
      ...props
    },
    ref
  ) => {
    const isControlled = controlledValue !== undefined;
    const [internalValue, setInternalValue] = useState(
      isControlled ? controlledValue : ""
    );
    const [isFocused, setIsFocused] = useState(false);
    const isFirstMount = useRef(true);

    useEffect(() => {
      if (isControlled) {
        setInternalValue(controlledValue || "");
      }
    }, [controlledValue, isControlled]);

    useEffect(() => {
      if (isFirstMount.current) {
        isFirstMount.current = false;
        return;
      }
      if (!onChange) return;
      const handler = setTimeout(() => {
        const syntheticEvent = {
          target: { value: internalValue },
          currentTarget: { value: internalValue },
          value: internalValue,
        };
        onChange(internalValue, syntheticEvent);
      }, debounceMs);

      return () => {
        clearTimeout(handler);
      };
    }, [internalValue, debounceMs, onChange]);

    const handleClear = () => {
      setInternalValue("");
      if (onChange) {
        const syntheticEvent = {
          target: { value: "" },
          currentTarget: { value: "" },
          value: "",
        };
        onChange("", syntheticEvent);
      }
      if (onClear) onClear();
    };

    return (
      <div
        className={`${styles.searchWrapper} ${
          isFocused ? styles.focused : ""
        } ${fullWidth ? styles.fullWidth : ""} ${className}`.trim()}
        style={style}
      >
        <Search size={16} className={styles.searchIcon} />
        <input
          ref={ref}
          type="text"
          className={styles.searchInput}
          placeholder={placeholder}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        />
        {internalValue && (
          <button
            type="button"
            className={styles.clearBtn}
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
